import React from 'react';
import { Box, Container, Typography, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

function TermsOfUse() {
  const sections = [
    {
      title: 'SECTION 1 — PROGRAM OVERVIEW & NATURE OF SERVICES',
      subsections: [
        {
          subtitle: '1.1 Virtual Evaluation Nature',
          points: [
            '1.1.1 All evaluation programs operate purely through a virtual trading environment.',
            '1.1.2 No real capital is traded in any global financial market.',
            '1.1.3 Fees paid by traders are access fees for participation and platform usage, not investment deposits.',
            '1.1.4 The Company does not manage funds, trade customer capital, or provide investment advisory services.'
          ]
        },
        {
          subtitle: '1.2 Objective of the Program',
          points: [
            '1.2.1 The evaluation challenges are designed to measure discipline, strategy, risk management, and trading psychology.',
            '1.2.2 Successful traders may be granted a Simulated Funded Account with Performance-Based Payouts.',
            '1.2.3 The program aims to reward consistency, rule-bound trading and responsible risk application.'
          ]
        },
        {
          subtitle: '1.3 Challenge Types',
          points: [
            {
              text: '1.3.1 Available challenge packages:',
              subPoints: [
                '(a) 25,000 USD Virtual Account',
                '(b) 50,000 USD Virtual Account',
                '(c) 100,000 USD Virtual Account'
              ]
            },
            '1.3.2 Each challenge follows identical rules, scaled only by lot-size limitations'
          ]
        }
      ]
    },
    {
      title: 'SECTION 2 — FUNDING PROCESS & REQUIREMENTS',
      subsections: [
        {
          subtitle: '2.1 Phase Completion Targets (Mandatory)',
          points: [
            '2.1.1 Phase 1 Target → 10% Profit',
            '2.1.2 Phase 2 Target → 5% Profit',
            '2.1.3 Achievement must occur without violating any rule listed in Section 3 or Section 4.',
            '2.1.4 Any rule violation leads to immediate challenge failure (ref TUF Code 4.1.1).'
          ]
        },
        {
          subtitle: '2.2 Drawdown Limits',
          points: [
            '2.2.1 Maximum Daily Drawdown (MDD): 5% of account equity/balance combined.',
            '2.2.2 Overall Drawdown (OD): 10% max at any time.',
            '2.2.3 Breach of either DD → instant evaluation termination (ref 4.1.2).'
          ]
        },
        {
          subtitle: '2.3 Minimum Trading Days Requirement',
          points: [
            '2.3.1 Minimum 3 separate trading days must be active in each phase.',
            '2.3.2 Profit target reached in one day without 3-day activity = not considered passed.',
            '2.3.3 Failure to meet minimum days before profit submission = incomplete evaluation (ref 4.2.2).'
          ]
        },
        {
          subtitle: '2.4 KYC & Verification',
          points: [
            '2.4.1 After passing both phases trader must submit a Funding Request.',
            '2.4.2 Mandatory Government-issued ID verification.',
            '2.4.3 The Company may require live video verification or IP/device match confirmation.',
            '2.4.4 Any mismatch or suspicious identity = compliance delay or rejection (ref 4.3.1).'
          ]
        }
      ]
    },
    {
      title: 'SECTION 3 — DETAILED TRADING RULES (STRICT ENFORCEMENT)',
      content: 'Violation of any sub-clause in Section 3 results in instant challenge termination.',
      subsections: [
        {
          subtitle: '3.1 Trade Execution & Stop Loss Guidelines',
          points: [
            '3.1.1 Every trade must include Stop-Loss (SL) & Take-Profit (TP) at the entry moment or within 3 minutes post-execution.',
            '3.1.2 SL editing is allowed anytime after entry, but an SL must exist within the first 3 minutes.',
            '3.1.3 Trades held without SL beyond 3 minutes = automatic violation (code 3.1.3).',
            '3.1.4 TP modifications cannot be reduced to secure artificial easier profit (no shrinking targets).',
            '3.1.5 Trailing Stop-Loss allowed and encouraged but must follow normal volatility logic.',
            '3.1.6 Risk-to-Reward ratio must remain sensible and non-manipulative (e.g., 1:0.1 types unacceptable).',
            '3.1.7 Over-leveraged trades or "all-in" behavior = risk violation (ref 3.3.2).'
          ]
        },
        {
          subtitle: '3.2 Scalping, Holding Time & Trade Duration',
          points: [
            '3.2.1 Opening & closing any trade within 2 minutes is prohibited.',
            '3.2.2 Trade exits near predefined SL/TP levels are acceptable.',
            '3.2.3 Premature scalps or instant entries/exits detected repeatedly = violation.',
            '3.2.4 Must allow market fluctuations; no micro-tick exploitation allowed.'
          ]
        },
        {
          subtitle: '3.3 Risk Management & Consistency',
          points: [
            '3.3.1 Risk must remain proportionate and consistent throughout evaluation.',
            '3.3.2 Lot size must not jump irrationally without strategy reasoning.',
            {
              text: '3.3.3 Daily maximum usage of lots:',
              subPoints: [
                '(a) 25k account → Max 3 Lots (Total Exposure)',
                '(b) 50k account → Max 7 Lots',
                '(c) 100k account → Max 10 Lots'
              ]
            },
            '3.3.4 Breach of lot cap = instant failure (code 3.3.4).'
          ]
        },
        {
          subtitle: '3.4 Prohibited Trading Activities',
          points: [
            '3.4.1 HFT / Tick Arbitrage / Latency Exploitation — STRICTLY banned.',
            '3.4.2 Copy-trading, shared strategies, hedging between accounts or group manipulation is prohibited.',
            '3.4.3 News Trading Ban: No trading 2 minutes before & 2 minutes after Red Folder events (ForexFactory).',
            '3.4.4 Misuse of server lag / price feed delay = fraud termination offence.',
            '3.4.5 Bots/Automation/AI order execution unless approved = disqualification.'
          ]
        },
        {
          subtitle: '3.5 Platform Integrity & IP Address Rules',
          points: [
            '3.5.1 Accounts are individual — one identity per program.',
            '3.5.2 Multiple device/IP/computer logins may trigger security review.',
            '3.5.3 Company may request proof of ownership anytime.',
            '3.5.4 Failure to verify identity = suspension/termination (ref 4.3.2).'
          ]
        }
      ]
    },
    {
      title: 'SECTION 4 — BREACH, VIOLATION & TERMINATION POLICY',
      subsections: [
        {
          subtitle: '4.1 Instant Termination Conditions',
          points: [
            '4.1.1 Violation of any Section 3 rule.',
            '4.1.2 Breach of drawdown limits.',
            '4.1.3 Fraudulent or exploitative activity.',
            '4.1.4 Refusal to cooperate in verification.'
          ]
        },
        {
          subtitle: '4.2 No Refund Policy',
          points: [
            '4.2.1 Fees paid are non-refundable under all circumstances.',
            '4.2.2 Early termination still counts as consumed service.'
          ]
        },
        {
          subtitle: '4.3 Company Rights',
          points: [
            '4.3.1 We may request KYC anytime during or after challenge.',
            '4.3.2 We may pause accounts for internal audit.',
            '4.3.3 We may modify rules without prior notice.'
          ]
        }
      ]
    },
    {
      title: 'SECTION 5 — LIABILITY & DISCLAIMER',
      subsections: [
        {
          subtitle: '',
          points: [
            '5.1 Educational use only — not financial advice.',
            '5.2 Traders are responsible for own decisions.',
            '5.3 Past results never guarantee future results.',
            '5.4 Participation = acceptance of risk of challenge failure.'
          ]
        }
      ]
    },
    {
      title: 'SECTION 6 — ACCEPTANCE',
      content: 'By accessing or using the platform, you confirm:',
      list: [
        '✔ You understand all rules with numbering reference.',
        '✔ Any violation terminates your challenge instantly.',
        '✔ Program is simulation-based & non-investment in nature'
      ]
    },
    {
      title: 'SECTION 7 — PURPOSE OF THE PLATFORM',
      subsections: [
        {
          subtitle: '',
          points: [
            '7.1 The purpose of the evaluation system is to assess trading skill, discipline, risk control and strategy execution in a controlled simulation environment designed for educational and performance-measurement reasons.',
            '7.2 The Company does not act as a broker, portfolio manager, investment advisor, or financial intermediary, nor does it provide access to trade real-market funds during evaluation stages.',
            '7.3 The primary objective is to identify capable traders who demonstrate consistency, rule obedience and risk-responsible trading behaviour, and reward them with simulated funded accounts based on performance.'
          ]
        }
      ]
    },
    {
      title: 'SECTION 8 — ELIGIBILITY & USER RESPONSIBILITIES',
      subsections: [
        {
          subtitle: '',
          points: [
            '8.1 Minimum age requirement to participate is 18 years, or legal majority age in user\'s jurisdiction — whichever is higher.',
            '8.2 Trader must provide accurate personal information and maintain compliance throughout the program.',
            '8.3 Participation may not be permitted where local law prohibits such activity. User is responsible for ensuring that participating in simulation programs is legally allowed in their country.',
            '8.4 The user agrees to operate only their own individual account and acknowledges that multiple persons operating one account is prohibited (ref 3.5.1 & 3.5.3).',
            '8.5 Any attempt to hide identity, falsify documents, or circumvent geo/IP checks may result in immediate account termination without refund (ref 4.1.1 & 4.3.1).'
          ]
        }
      ]
    },
    {
      title: 'SECTION 9 — INTELLECTUAL PROPERTY RIGHTS',
      subsections: [
        {
          subtitle: '',
          points: [
            '9.1 All platform content including but not limited to software, user interface, branding, proprietary trading systems, evaluation structures, data feeds, documentation, text, code, graphics, and operational models belongs to the Company.',
            '9.2 Users may not copy, reproduce, resell, distribute, reverse-engineer or exploit any part of the platform unless explicit written permission is granted.',
            '9.3 Educational materials and evaluation tools provided to traders remain intellectual property of the Company.',
            '9.4 Any violation of this clause may result in civil action, legal remedy, account termination, and claims for damages.'
          ]
        }
      ]
    },
    {
      title: 'SECTION 10 — LIABILITY & PERFORMANCE DISCLAIMER',
      content: '(extension of previous disclaimer section)',
      subsections: [
        {
          subtitle: '',
          points: [
            '10.1 Trading involves risk. Evaluation results do not guarantee success in real markets or future earnings.',
            {
              text: '10.2 The Company is not liable for:',
              subPoints: [
                '(a) Market conditions, slippage, news volatility, technical outages.',
                '(b) User decisions, emotions, risk choices or strategy outcomes.',
                '(c) Profit losses or evaluation fees upon failure of challenge.'
              ]
            },
            '10.3 Platform downtime, feed latency or server maintenance may occur occasionally. Such events do not warrant refunds, extensions, or compensation unless officially approved.',
            '10.4 Participation implies you understand that funding reward is conditional and subject to rule adherence (Section 3 & 4).'
          ]
        }
      ]
    },
    {
      title: 'SECTION 11 — DATA PROTECTION & PRIVACY',
      subsections: [
        {
          subtitle: '',
          points: [
            {
              text: '11.1 The Company collects and processes personal data solely for:',
              subPoints: [
                '(a) Account creation & maintenance',
                '(b) Payment & billing records',
                '(c) KYC identity verification',
                '(d) Fraud prevention & security monitoring',
                '(e) Audit and compliance requirements'
              ]
            },
            {
              text: '11.2 Data is stored securely and is not shared with third parties except:',
              subPoints: [
                '(a) Legal authorities upon lawful request',
                '(b) Verification vendors for KYC compliance',
                '(c) Service providers essential for platform operation'
              ]
            },
            '11.3 Users may request account data updates or deletion unless required for legal record retention.',
            '11.4 The Company may monitor IP addresses, device IDs, execution behavior, and platform usage for integrity and abuse prevention (ref 3.5.1 & 3.5.2).',
            '11.5 By using the platform, you consent to the collection, processing, and storage of your data as outlined above.'
          ]
        }
      ]
    },
    {
      title: 'SECTION 12 — HIGH-RISK FINANCIAL MARKET DISCLAIMER',
      subsections: [
        {
          subtitle: '',
          points: [
            '12.1 Trading and speculation in Forex, Cryptocurrency, Commodities, Stocks, Indices, and other financial instruments involve a high level of risk, including the potential loss of capital. Market prices can fluctuate rapidly and unpredictably due to geopolitical events, economic releases, liquidity conditions, technical failures, and other global circumstances beyond trader or Company control.',
            '12.2 Trading is not suitable for all individuals. By participating, the user acknowledges that only those who fully understand the risks, have prior experience or education in financial markets, or have taken it upon themselves to learn trading fundamentals should engage in any form of simulated or real-market related activity.',
            '12.3 The Company strongly advises Users to ensure they are financially capable, mentally prepared, and well-versed in trading mechanisms, risk exposure, order execution, margin concepts, leverage risks, and volatility management before entering the evaluation.',
            '12.4 The trader understands and agrees that the Company is not responsible for any financial decisions made by the trader outside this simulated environment and does not promise or imply guaranteed returns, income, or success in real trading markets.',
            '12.5 Traders should never participate using funds they cannot afford to lose. Losses, failed evaluations, psychological pressure, and drawdowns are all natural outcomes of trading activity.',
            {
              text: '12.6 Participation is voluntary, and by accessing the platform, the trader confirms full awareness that:',
              subPoints: [
                '(a) They may lose the evaluation fee if rules are violated.',
                '(b) Trading performance can result in challenge failure.',
                '(c) Profit potential is not guaranteed under any circumstances.',
                '(d) Future success in financial markets cannot be assumed based on evaluation results.'
              ]
            },
            '12.7 The user is advised to seek independent financial advice if uncertain about risks, trading knowledge, or psychological preparedness prior to participation.'
          ]
        }
      ]
    }
  ];

  const planData = [
    { plan: 'Starter', accountSize: '$25,000', fee: '$79' },
    { plan: 'Advanced', accountSize: '$50,000', fee: '$134' },
    { plan: 'Pro', accountSize: '$100,000', fee: '$256' }
  ];

  return (
    <Box sx={{ bgcolor: '#131722', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ fontWeight: 700, color: '#fff', mb: 2, fontSize: '2.5rem' }}>
            Terms of Use
          </Typography>
          <Typography variant="body1" sx={{ color: '#d1d4dc', mb: 1, fontSize: '1.1rem', lineHeight: 1.8 }}>
            Welcome to X Funding ("we," "our," or "us"). By accessing or using our website and services ("Platform"), you agree to the following Terms of Use. Please read them carefully before registering or participating in any challenge or evaluation.
          </Typography>
          <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic', mt: 2, fontSize: '0.95rem' }}>
            Last updated: 14.05.2025
          </Typography>
        </Box>

        {/* Content Sections */}
        <Box sx={{ bgcolor: '#1e222d', borderRadius: 2, p: 4, border: '1px solid #2a2e39' }}>
          {sections.map((section, index) => (
            <Box key={index} sx={{ mb: 5 }}>
              <Typography variant="h5" sx={{ color: '#3b82f6', fontWeight: 600, mb: 2.5, fontSize: '1.4rem' }}>
                {section.title}
              </Typography>
              
              {section.content && (
                <Typography variant="body1" sx={{ color: '#d1d4dc', mb: 2, fontSize: '1.05rem', lineHeight: 1.8 }}>
                  {section.content}
                </Typography>
              )}

              {/* List Items */}
              {section.list && (
                <Box component="ul" sx={{ color: '#d1d4dc', pl: 3, mb: 2 }}>
                  {section.list.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '12px' }}>
                      <Typography variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.8 }}>{item}</Typography>
                    </li>
                  ))}
                </Box>
              )}

              {/* Table for Challenge Plans */}
              {section.hasTable && (
                <Box sx={{ mb: 2 }}>
                  <TableContainer component={Paper} sx={{ bgcolor: '#131722', border: '1px solid #2a2e39' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'rgba(59,130,246,0.1)' }}>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, borderBottom: '2px solid #3b82f6', fontSize: '1.05rem' }}>Plan</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, borderBottom: '2px solid #3b82f6', fontSize: '1.05rem' }}>Account Size</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, borderBottom: '2px solid #3b82f6', fontSize: '1.05rem' }}>Challenge Fee</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {planData.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ color: '#fff', borderBottom: '1px solid #2a2e39', fontSize: '1rem' }}>{row.plan}</TableCell>
                            <TableCell sx={{ color: '#d1d4dc', borderBottom: '1px solid #2a2e39', fontSize: '1rem' }}>{row.accountSize}</TableCell>
                            <TableCell sx={{ color: '#d1d4dc', borderBottom: '1px solid #2a2e39', fontSize: '1rem' }}>{row.fee}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="body2" sx={{ color: '#d1d4dc', mt: 2, fontStyle: 'italic', fontSize: '0.95rem', lineHeight: 1.7 }}>
                    Fees are non-refundable once the challenge begins. Each plan includes access to the same trading conditions, with differences only in account size and scaling potential.
                  </Typography>
                </Box>
              )}

              {/* Subsections for Trading Rules */}
              {section.subsections && (
                <Box sx={{ pl: 2 }}>
                  {section.subsections.map((sub, subIdx) => (
                    <Box key={subIdx} sx={{ mb: 3 }}>
                      {sub.subtitle && (
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1.5, fontSize: '1.15rem' }}>
                          {sub.subtitle}
                        </Typography>
                      )}
                      <Box sx={{ color: '#d1d4dc', pl: 2 }}>
                        {sub.points.map((point, pointIdx) => (
                          <Box key={pointIdx}>
                            <Typography variant="body1" sx={{ mb: 1.5, fontSize: '1.05rem', lineHeight: 1.8 }}>
                              {typeof point === 'string' ? point : point.text}
                            </Typography>
                            {typeof point === 'object' && point.subPoints && (
                              <Box sx={{ pl: 4, mb: 1.5 }}>
                                {point.subPoints.map((subPoint, subPointIdx) => (
                                  <Typography key={subPointIdx} variant="body2" sx={{ mb: 0.8, fontSize: '1rem', lineHeight: 1.7 }}>
                                    {subPoint}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Footer text */}
              {section.footer && (
                <Typography variant="body1" sx={{ color: '#d1d4dc', mt: 2, fontStyle: 'italic', fontSize: '1.05rem', lineHeight: 1.8 }}>
                  {section.footer}
                </Typography>
              )}

              {index < sections.length - 1 && <Divider sx={{ my: 4, borderColor: '#2a2e39' }} />}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default TermsOfUse;
